'use client';

import { FC, useCallback, useMemo } from 'react';
import { useRouter } from '@/lib/router';
import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';
import { useModal } from '@pcmp/react-providers';
import Button from '@ingka/button';
import Search from '@ingka/search';
import ArrowDownToBaseIcon from '@ingka/ssr-icon/paths/arrow-down-to-base';
import { useActiveLocation } from '@/redux/features/shopping-bag/hooks';
import { Item, Location, OrderType } from '@/redux/back-dashboard-api';
import { ProductWithStock } from '@/lib/utils/database';
import { useQueryParamState } from '@/lib/hooks/url';
import { useLocationProducts } from '@/redux/back-dashboard-api/hooks';
import { downloadLink } from '@/lib/utils/router';
import { LoadingFullScreen } from '@/components/helpers/loading';
import { GenericErrorCard } from '@/components/data/error';
import {
  InnerPageContainer,
  PageContainer,
  PageContainerHeader,
} from '@/components/layouts/page-webshop';
import { OrderOnBehalfButton } from '@/components/buttons/order-on-behalf-button';
import { useWebshopMode } from '@/components/page-widgets/webshop/context';
import { WardrobeHeader } from '@/components/page-widgets/webshop/wardrobe/header';
import {
  ShopProduct,
  ShopProductGrid,
} from '@/components/containers/card-shop-product';

const mapProduct = (product: ProductWithStock) => {
  const searchProps = product.items.reduce(
    (acc, item) => {
      // item.categories.forEach((category) => acc.categories.add(category));
      (item.categories || []).forEach((category) =>
        acc.categories.add(category)
      );
      acc.type.add(item.type);
      acc.productName.add(item.productName);
      acc.description.add(item.description);
      acc.ref.add(product.itemRef.sku);
      acc.name.add(product.itemRef.displayName);
      Object.entries(item.combinations).forEach((key, value) =>
        acc.combinations.add(`${key}:${value}`)
      );
      Object.entries(item.properties).forEach((key, value) =>
        acc.properties.add(`${key}:${value}`)
      );
      return acc;
    },
    {
      categories: new Set(),
      type: new Set(),
      productName: new Set(),
      description: new Set(),
      ref: new Set(),
      name: new Set(),
      combinations: new Set(),
      properties: new Set(),
    }
  );

  return {
    ...product,
    searchQuery: Object.entries(searchProps)
      .map(([key, value]) => `${key}=${[...value].join(',')}`)
      .join('&'),
  };
};

const DownloadableDocuments: FC = () => {
  // Hooks
  const { t } = useTranslation();

  return (
    <div className="flex flex-row items-center space-x-2 pb-4">
      <Button
        onClick={() =>
          downloadLink('/documents/clothing-guide.pdf', 'clothing-guide.pdf')
        }
        text={t('label.clothingGuide')}
        type="secondary"
        size="small"
        iconPosition="trailing"
        ssrIcon={ArrowDownToBaseIcon}
      />

      <Button
        onClick={() =>
          downloadLink('/documents/find-your-size.pdf', 'find_your_size.pdf')
        }
        text={t('label.findSize')}
        type="secondary"
        size="small"
        iconPosition="trailing"
        ssrIcon={ArrowDownToBaseIcon}
      />
    </div>
  );
};

const ProductFilters: FC<{
  value?: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  // Hooks
  const { t } = useTranslation();

  return (
    <Search
      id="search"
      className="search"
      ariaLabel={t('aria.search')}
      clearBtnText={t('aria.clearSearch')}
      placeholder={t('label.search')}
      value={value}
      onSearch={(_, context) => {
        onChange(context.value || '');
      }}
      onChange={(e) => {
        onChange(e.target.value || '');
      }}
      submitBtnText={t('aria.submit')}
    />
  );
};

const ShopItems: FC<{
  location: Location;
  category?: string;
  search?: string;
  onAddToCart?: (item: Item, amount: number) => void;
}> = ({ location, category, search, onAddToCart }) => {
  // Hooks
  const { t } = useTranslation();
  const router = useRouter();
  const { openModal } = useModal();
  const { showStock, orderType, updateItem, getItemPath } = useWebshopMode();
  const {
    data: productsWithStock,
    isLoading,
    error,
  } = useLocationProducts(location, {
    onlyInWarehouse: showStock,
  });
  // Data
  // Append "searchQuery" to each product for searching
  // This variable will be used to filter products based on search input and its
  // format is:
  // categories=category1,category2&type=type1,type2&productName=...
  const searchableProducts = useMemo(
    () => productsWithStock.map(mapProduct),
    [productsWithStock]
  );
  // Filtered items based on selected category
  const categoryFilteredProducts = useMemo(() => {
    if (!category) return searchableProducts;
    return searchableProducts.filter((product) =>
      product.items.some((item) => `${item.category}` === category)
    );
  }, [searchableProducts, category]);
  // Filtered items based on search input
  const filteredProducts = useMemo(() => {
    const result = categoryFilteredProducts
      // Sort by stock availability - products with available stock come first
      .sort((a, b) => {
        const aHasStock = a.items.some((item) => item.stock.available > 0);
        const bHasStock = b.items.some((item) => item.stock.available > 0);

        if (aHasStock && !bHasStock) return -1;
        if (!aHasStock && bHasStock) return 1;
        return 0;
      });
    if (!search) return result;
    const test = new RegExp(search, 'i');
    return result.filter(({ searchQuery }) => test.test(searchQuery));
  }, [categoryFilteredProducts, search]);
  // Event handlers
  const handleProductClick = useCallback(
    async (product: ProductWithStock, preferredItem: Item) => {
      router.push(getItemPath(location.slug, preferredItem.slug));
    },
    [location.slug, router, getItemPath]
  );
  const handleCombinationPicker = useCallback(
    async (product: ProductWithStock, preferredItem: Item) => {
      const component = await import(
        '@/components/siders/product-combination'
      ).then((m) => m.default);

      openModal({
        component,
        props: {
          product,
          preferredItem,
          orderType,
          onSelect:
            onAddToCart ||
            ((item, amount) => {
              updateItem({ id: item._id, amount });
            }),
        },
      });
    },
    [onAddToCart, openModal, orderType, updateItem]
  );

  if (error) return <GenericErrorCard className="pt-6" error={error} card />;

  if (isLoading || !location) return <LoadingFullScreen />;

  return (
    <div className="flex flex-row items-center w-full">
      {filteredProducts.length === 0 ? (
        <p className="m-auto pt-8">
          <b>{t('page.shop.noProductsFound')}</b>
        </p>
      ) : (
        <ShopProductGrid>
          {filteredProducts.map((product) => (
            <ShopProduct
              key={product.itemRef.productName}
              product={product}
              onClick={handleProductClick}
              onAddToCart={handleCombinationPicker}
            />
          ))}
        </ShopProductGrid>
      )}
    </div>
  );
};

export const ProductShopPageContent: FC<{
  onAddToCart?: (item: Item, amount: number) => void;
  showOrderOnBehalf?: boolean;
  showDownloadableDocuments?: boolean;
}> = ({
  onAddToCart,
  showOrderOnBehalf = true,
  showDownloadableDocuments = true,
}) => {
  const [selectedCategory] = useQueryParamState('category');
  const [search, setSearch] = useDebounceValue('', 300);
  // Data
  const { data: location, isLoading, error } = useActiveLocation();
  const { orderType } = useWebshopMode();

  if (isLoading) {
    return <LoadingFullScreen />;
  }

  if (error || !location)
    return <GenericErrorCard className="pt-6" error={error} card />;

  return (
    <PageContainer id="shopPage" disablePadding>
      {orderType === OrderType.Inventory && <WardrobeHeader />}

      <PageContainerHeader>
        <div className="flex flex-row justify-between items-center flex-wrap w-full gap-4 md:gap-0">
          <div className="w-full md:max-w-[400px]">
            <ProductFilters onChange={setSearch} />
          </div>

          {showOrderOnBehalf && (
            <div className="flex justify-center">
              <OrderOnBehalfButton />
            </div>
          )}
        </div>
      </PageContainerHeader>

      <InnerPageContainer>
        {showDownloadableDocuments && <DownloadableDocuments />}

        {location && (
          <ShopItems
            location={location}
            category={selectedCategory}
            search={search}
            onAddToCart={onAddToCart}
          />
        )}
      </InnerPageContainer>
    </PageContainer>
  );
};
